import React, { useState } from "react";
import { BadgeCheck, CalendarClock, UserPlus, ScrollText, Milestone } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDateFr } from "@/components/placements/placementsShared";
import { INTERLOCUTOR_ROLES, MILESTONES } from "./discussionsShared";
import { useLang } from "@/i18n";

export function StageChangeDialog({ open, onOpenChange, detail, initialStage, onSaved }) {
  const { tf, lang } = useLang();
  const [target, setTarget] = useState(null);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [naNote, setNaNote] = useState("");
  const [naDue, setNaDue] = useState("");
  const [busy, setBusy] = useState(false);
  const current = detail?.current_milestone;
  React.useEffect(() => {
    if (open) {
      setTarget(initialStage || null);
      const d = new Date();
      setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
      setNote(""); setNaNote(""); setNaDue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialStage]);
  if (!detail) return null;
  const targetLabel = MILESTONES.find((m) => m.id === target)?.label || "";
  const isCurrent = target === current;
  const halfAction = Boolean(naNote.trim()) !== Boolean(naDue);
  const confirm = async () => {
    if (busy || !target || isCurrent) return;
    if (halfAction) { toast.error(tf("Libellé et échéance requis")); return; }
    setBusy(true);
    try {
      const r = await api.post(`/discussions/${detail.id}/stage`, {
        milestone_type: target, date: date || null, note,
        next_action: naNote.trim() && naDue ? { note: naNote, due_at: naDue } : null,
      });
      toast.success(r.data.already ? tf("Cette discussion est déjà à cette étape.") : tf("Étape de la discussion modifiée"));
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tf("Erreur"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] max-h-[88vh] overflow-y-auto" data-testid="stage-change-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Milestone className="h-4 w-4 text-info" /> {tf("Modifier l'étape de la discussion")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1.5">
            <label className="text-xs font-medium">{tf("Étape")}</label>
            <Select value={target || ""} onValueChange={setTarget}>
              <SelectTrigger className="h-9" data-testid="stage-select"><SelectValue placeholder={tf("Choisir une étape")} /></SelectTrigger>
              <SelectContent>
                {MILESTONES.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`stage-option-${m.id}`}>
                    {tf(m.label)}{m.id === current ? ` — ${tf("étape actuelle")}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {target && !isCurrent && (
            <p className="text-xs text-muted-foreground leading-relaxed" data-testid="stage-change-question">
              {tf("Souhaitez-vous faire passer cette discussion à l'étape")}{lang === "fr" ? ` « ${tf(targetLabel)} » ?` : ` "${tf(targetLabel)}"?`}
            </p>
          )}
          {isCurrent && (
            <p className="text-xs text-warning" data-testid="stage-already-current">{tf("Cette discussion est déjà à cette étape.")}</p>
          )}
          <div className="space-y-1.5">
            <label className="text-xs font-medium" htmlFor="stage-date">{tf("Date de progression")}</label>
            <Input id="stage-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="h-9" data-testid="stage-date-input" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium" htmlFor="stage-note">{tf("Note interne (optionnel)")}</label>
            <Textarea id="stage-note" value={note} onChange={(e) => setNote(e.target.value)}
              className="min-h-[52px] text-sm" data-testid="stage-note-input" />
          </div>
          <div className="rounded-xl border border-border dark:border-white/10 p-3 space-y-2">
            <div className="text-xs font-medium text-muted-foreground">{tf("Prochaine action (optionnel)")}</div>
            <Input value={naNote} onChange={(e) => setNaNote(e.target.value)}
              placeholder={tf("Ex. : Préparer le rendez-vous avec la famille")} className="h-9 text-sm"
              data-testid="stage-next-action-note" />
            <Input type="date" value={naDue} onChange={(e) => setNaDue(e.target.value)} className="h-9 text-sm"
              data-testid="stage-next-action-due" />
            {halfAction && (
              <p className="text-[10px] text-warning" data-testid="stage-next-action-hint">{tf("Libellé et échéance requis")}</p>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="stage-cancel-btn">{tf("Annuler")}</Button>
          <Button onClick={confirm} disabled={busy || !target || isCurrent}
            className="bg-info hover:bg-info/90 text-white" data-testid="stage-confirm-btn">
            {busy ? tf("Enregistrement…") : tf("Confirmer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConfirmMandateDialog({ open, onOpenChange, detail, onConfirmed }) {
  const { tf } = useLang();
  const [effectiveDate, setEffectiveDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const a = detail?.athlete || {};
  const done = (detail?.milestones || []).filter((m) => m.status === "completed").length;
  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/discussions/${detail.id}/confirm-mandate`,
        { effective_date: effectiveDate || null, note });
      toast.success(r.data.already ? tf("Mandat déjà confirmé") : tf("Mandat confirmé — l'athlète passe en « Mandat signé »"));
      onOpenChange(false);
      onConfirmed?.();
    } catch { toast.error(tf("Erreur lors de la confirmation")); } finally { setBusy(false); }
  };
  if (!detail) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[88vh] overflow-y-auto" data-testid="confirm-mandate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BadgeCheck className="h-4 w-4 text-info" /> {tf("Confirmer le mandat")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-xl border border-border dark:border-white/10 p-3 space-y-1.5">
            <div className="flex justify-between gap-3">
              <span className="text-xs text-muted-foreground">{tf("Athlète")}</span>
              <span className="font-medium">{a.name}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-xs text-muted-foreground">{tf("Discussion depuis")}</span>
              <span className="font-medium">{fmtDateFr(detail.started_at)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-xs text-muted-foreground">{tf("Jalons franchis")}</span>
              <span className="font-medium">{done} / {MILESTONES.length}</span>
            </div>
            {detail.proposed_terms?.mandate_type && (
              <div className="flex justify-between gap-3">
                <span className="text-xs text-muted-foreground">{tf("Type de mandat")}</span>
                <span className="font-medium">{detail.proposed_terms.mandate_type}</span>
              </div>
            )}
            {detail.contract && (
              <div className="flex justify-between gap-3">
                <span className="text-xs text-muted-foreground">{tf("Contrat lié")}</span>
                <span className="font-medium truncate">{detail.contract.title}</span>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {tf("L'athlète passera de « En discussion » à « Mandat signé » dans la Pipeline. La discussion sera clôturée et son historique conservé.")}
          </p>
          <div className="space-y-1.5">
            <label className="text-xs font-medium" htmlFor="mandate-date">{tf("Date d'effet (optionnel)")}</label>
            <Input id="mandate-date" type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
              className="h-9" data-testid="mandate-effective-date" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium" htmlFor="mandate-note">{tf("Note (optionnel)")}</label>
            <Textarea id="mandate-note" value={note} onChange={(e) => setNote(e.target.value)}
              className="min-h-[56px] text-sm" data-testid="mandate-note" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tf("Annuler")}</Button>
          <Button onClick={confirm} disabled={busy} className="bg-info hover:bg-info/90 text-white"
            data-testid="mandate-confirm-btn">
            {busy ? tf("Confirmation…") : tf("Confirmer le mandat")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddActionDialog({ open, onOpenChange, discussionId, onSaved }) {
  const { tf } = useLang();
  const [note, setNote] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!note.trim() || !dueAt) { toast.error(tf("Libellé et échéance requis")); return; }
    setBusy(true);
    try {
      await api.post(`/discussions/${discussionId}/actions`, { note, due_at: dueAt });
      toast.success(tf("Action planifiée"));
      setNote(""); setDueAt(""); onOpenChange(false); onSaved?.();
    } catch { toast.error(tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]" data-testid="add-action-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4" /> {tf("Planifier une action")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={tf("Ex. : Préparer le rendez-vous avec la famille")} data-testid="action-note-input" />
          <Input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} data-testid="action-due-input" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tf("Annuler")}</Button>
          <Button onClick={save} disabled={busy} data-testid="action-save-btn">{tf("Planifier")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddInterlocutorDialog({ open, onOpenChange, discussionId, onSaved }) {
  const { tf } = useLang();
  const [name, setName] = useState("");
  const [role, setRole] = useState("Autre");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error(tf("Nom requis")); return; }
    setBusy(true);
    try {
      const r = await api.post(`/discussions/${discussionId}/interlocutors`, { name, role, phone, email });
      toast.success(r.data.already ? tf("Interlocuteur déjà présent") : tf("Interlocuteur ajouté"));
      setName(""); setPhone(""); setEmail(""); onOpenChange(false); onSaved?.();
    } catch { toast.error(tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]" data-testid="add-interlocutor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" /> {tf("Ajouter un interlocuteur")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={tf("Nom complet")}
            data-testid="interlocutor-name-input" />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="interlocutor-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {INTERLOCUTOR_ROLES.map((r) => <SelectItem key={r} value={r}>{tf(r)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={tf("Téléphone (optionnel)")}
            data-testid="interlocutor-phone-input" />
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={tf("Email (optionnel)")}
            data-testid="interlocutor-email-input" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tf("Annuler")}</Button>
          <Button onClick={save} disabled={busy} data-testid="interlocutor-save-btn">{tf("Ajouter")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TermsDialog({ open, onOpenChange, detail, onSaved }) {
  const { tf } = useLang();
  const t = detail?.proposed_terms || {};
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    if (open) setForm({
      mandate_type: t.mandate_type || "", duration: t.duration || "", commission: t.commission || "",
      services: t.services || "", territory: t.territory || "", exclusivity: t.exclusivity || "",
      start_date: t.start_date || "", status: t.status || "En négociation", notes: t.notes || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/discussions/${detail.id}/terms`, form);
      toast.success(tf("Conditions enregistrées"));
      onOpenChange(false); onSaved?.();
    } catch { toast.error(tf("Erreur")); } finally { setBusy(false); }
  };
  if (!form) return null;
  const F = ({ k, label, placeholder }) => (
    <div className="space-y-1">
      <label className="text-xs font-medium" htmlFor={`term-${k}`}>{label}</label>
      <Input id={`term-${k}`} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
        placeholder={placeholder} className="h-9 text-sm" data-testid={`term-${k}`} />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[88vh] overflow-y-auto" data-testid="terms-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScrollText className="h-4 w-4" /> {tf("Conditions envisagées")}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <F k="mandate_type" label={tf("Type de mandat")} placeholder={tf("Ex. : Représentation")} />
          <F k="duration" label={tf("Durée")} placeholder={tf("Ex. : 2 ans (à confirmer)")} />
          <F k="commission" label={tf("Commission")} placeholder={tf("Ex. : À discuter")} />
          <F k="services" label={tf("Services")} placeholder={tf("Ex. : Représentation complète")} />
          <F k="territory" label={tf("Territoire (optionnel)")} placeholder="" />
          <F k="exclusivity" label={tf("Exclusivité (optionnel)")} placeholder="" />
          <div className="space-y-1">
            <label className="text-xs font-medium" htmlFor="term-start">{tf("Date d'effet envisagée")}</label>
            <Input id="term-start" type="date" value={form.start_date}
              onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
              className="h-9 text-sm" data-testid="term-start_date" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">{tf("Statut")}</label>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="h-9 text-sm" data-testid="term-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["À discuter", "En négociation", "Accord de principe"].map((s) => (
                  <SelectItem key={s} value={s}>{tf(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          placeholder={tf("Notes (optionnel)")} className="min-h-[56px] text-sm mt-1" data-testid="term-notes" />
        <p className="text-[10px] text-muted-foreground/70">
          {tf("Ces conditions restent des éléments de discussion — aucun contrat n'est créé.")}
        </p>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tf("Annuler")}</Button>
          <Button onClick={save} disabled={busy} data-testid="terms-save-btn">{tf("Enregistrer")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
